// MessageTemplatesPage.js — Member-facing Message Templates library (flat list, no folder rail).
//
// D-02: Single-pane layout — no folder rail.
// Owns editorState (null | { template } | { template: null }) to drive the overlay editor.
// All CRUD routes through messagingService — never calls Firestore directly.
//
// Props:
//   workspaceId  {string}   — authenticated workspace ID
//   templates    {Array}    — messageTemplates from useWorkspaceData (plan 14-03)
//   showToast    {Function} — (message, type) toast helper

import React from 'react';
import usePendingAction from '../../../hooks/usePendingAction';
import { Button } from '../../ui/primitives';
import MessageTemplateCard from './MessageTemplateCard';
import MessageTemplateEditorPane from './MessageTemplateEditorPane';
import {
    createMessageTemplate,
    deleteMessageTemplate,
} from '../../../services/messagingService';
import { useMessageTemplatesLease } from '../../../store/hooks/useMessageTemplatesLease';

// ─── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ onNew }) {
    return (
        <div className="flex flex-col items-center justify-center h-full py-16 px-6 text-center">
            <p
                className="text-sm font-semibold mb-1"
                style={{ color: 'var(--text-primary)' }}
            >
                No message templates yet
            </p>
            <p
                className="text-xs mb-4"
                style={{ color: 'var(--text-muted)' }}
            >
                Create reusable plain-text templates for SMS and RCS messages.
                <br />
                Use tokens to personalize each message at send time.
            </p>
            <button
                type="button"
                onClick={onNew}
                className="inline-flex items-center h-9 px-4 rounded-md text-sm font-semibold text-white"
                style={{ background: 'var(--color-accent)' }}
            >
                New template
            </button>
        </div>
    );
}

// ─── Delete confirm ────────────────────────────────────────────────────────────

function DeleteConfirmModal({ templateName, onConfirm, onCancel, deleting }) {
    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.4)' }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-msg-template-title"
        >
            <div className="w-full max-w-sm mx-4 rounded-xl border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] shadow-xl flex flex-col">
                <div className="px-6 pt-6 pb-4">
                    <h2
                        id="delete-msg-template-title"
                        className="text-base font-semibold text-[color:var(--text-primary)]"
                    >
                        Delete &ldquo;{templateName}&rdquo;?
                    </h2>
                    <p className="text-sm text-[color:var(--text-muted)] mt-1">
                        This template will be permanently removed. This action cannot be undone.
                    </p>
                </div>
                <div className="flex items-center justify-end gap-2 px-6 pb-6">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={deleting}
                        className="inline-flex items-center h-9 px-4 rounded-md text-sm font-semibold border border-[color:var(--border-color)] bg-transparent hover:bg-[color:var(--surface-hover)] transition-colors disabled:opacity-50"
                        style={{ color: 'var(--text-primary)' }}
                    >
                        Cancel
                    </button>
                    <Button
                        variant="danger"
                        onClick={onConfirm}
                        loading={deleting}
                        loadingLabel="Deleting…"
                    >
                        Delete template
                    </Button>
                </div>
            </div>
        </div>
    );
}

// ─── MessageTemplatesPage ──────────────────────────────────────────────────────

export default function MessageTemplatesPage({
    workspaceId,
    showToast,
}) {
    // D-20 (39-17, Task 1): messageTemplates leases per-route via the store
    // instead of the App.js-threaded prop.
    const templates = useMessageTemplatesLease(workspaceId);
    // null         → editor closed
    // { template } → editing existing
    // { template: null } → creating new
    const [editorState, setEditorState] = React.useState(null);
    const [deleteTarget, setDeleteTarget] = React.useState(null); // template to delete

    const handleNew = () => setEditorState({ template: null });
    const handleEdit = (template) => setEditorState({ template });
    const handleCloseEditor = () => setEditorState(null);

    const handleDuplicate = async (template) => {
        try {
            const { id, createdAt, updatedAt, ...rest } = template;
            const payload = {
                ...rest,
                name: `Copy of ${(rest.name || '').trim() || 'Untitled Template'}`,
                createdAt: Date.now(),
                updatedAt: Date.now(),
            };
            await createMessageTemplate(workspaceId, payload);
            showToast?.('Template duplicated.', 'success');
        } catch (err) {
            showToast?.(err?.message || "Couldn't duplicate the template. Try again.", 'error');
        }
    };

    const handleDeleteRequest = (template) => setDeleteTarget(template);
    const handleDeleteCancel = () => setDeleteTarget(null);

    const { run: runDeleteConfirm, pending: deleting } = usePendingAction(async () => {
        try {
            await deleteMessageTemplate(workspaceId, deleteTarget.id);
            showToast?.('Template deleted.', 'success');
            setDeleteTarget(null);
        } catch (err) {
            showToast?.(err?.message || "Couldn't delete the template. Try again.", 'error');
        }
    });
    const handleDeleteConfirm = () => (deleteTarget ? runDeleteConfirm() : undefined);

    return (
        <div className="flex flex-col h-full" style={{ background: 'var(--body-bg)' }}>
            {/* Page header */}
            <div data-tour="message-templates-header" className="flex items-center justify-between px-6 pt-6 pb-4">
                <div>
                    <h1
                        className="text-xl font-semibold leading-[1.3]"
                        style={{ color: 'var(--text-primary)' }}
                    >
                        Message Templates
                    </h1>
                    <p
                        className="text-xs mt-0.5"
                        style={{ color: 'var(--text-muted)' }}
                    >
                        Create and reuse plain-text SMS and RCS message templates with dynamic tokens.
                    </p>
                </div>
                <button
                    data-tour="message-templates-add"
                    type="button"
                    onClick={handleNew}
                    className="inline-flex items-center h-9 px-4 rounded-md text-sm font-semibold text-white shrink-0"
                    style={{ background: 'var(--color-accent)' }}
                >
                    New template
                </button>
            </div>

            {/* Template grid */}
            <div data-tour="message-templates-list" className="flex-1 overflow-y-auto" style={{ background: 'var(--surface-primary)' }}>
                {templates.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
                        {templates.map((t) => (
                            <MessageTemplateCard
                                key={t.id}
                                template={t}
                                onEdit={handleEdit}
                                onDuplicate={handleDuplicate}
                                onDelete={handleDeleteRequest}
                            />
                        ))}
                    </div>
                ) : (
                    <EmptyState onNew={handleNew} />
                )}
            </div>

            {/* Overlay editor */}
            {editorState && (
                <MessageTemplateEditorPane
                    workspaceId={workspaceId}
                    template={editorState.template}
                    onClose={handleCloseEditor}
                    onSaved={handleCloseEditor}
                    onDeleted={handleCloseEditor}
                    showToast={showToast}
                />
            )}

            {/* Delete confirmation modal */}
            {deleteTarget && (
                <DeleteConfirmModal
                    templateName={deleteTarget.name || 'this template'}
                    onConfirm={handleDeleteConfirm}
                    onCancel={handleDeleteCancel}
                    deleting={deleting}
                />
            )}
        </div>
    );
}
